'use client'

import { useRef } from 'react'

import { Alert } from '@src/app/[country]/[lang]/checkout/modules/Alert'
import ProductGallery from '@src/components/ProductConfigurator/ProductGallery'
import { useQuantityError } from '@src/components/ProductConfigurator/hooks/useQuantityError'
import { useScrollHandler } from '@src/components/ProductConfigurator/hooks/useScrollHandler'
import { FacetRenderer } from '@src/components/ProductConfigurator/modules/FacetRenderer/FacetRenderer'
import { ProductDescriptionSection } from '@src/components/ProductConfigurator/modules/ProductDescription/ProductDescriptionSection'
import { useEffectOnce } from '@src/hooks/useEffectOnce'
import { useLoadings } from '@src/hooks/useLoadings'
import { usePDP } from '@src/hooks/usePDP'
import { useLocale } from '@src/i18n/LocaleProvider'
import { formatCartAmount } from '@src/utils/cart.util'
import { EXPERIMENTS } from '@src/utils/constants.util'
import { useExperimentContext } from '@src/utils/experimentProvider'
import { getMaxQuantity, getValidQuantity, getVariantStrikedPrice } from '@src/utils/product.util'
import { cn } from '@src/utils/tailwind'

import { Button } from '../Button/Button'
import ProductGalleryV2 from './modules/ProductGalleryV2/ProductGalleryV2'
import { ProductInfo } from './modules/ProductInfo/ProductInfo'
import { QuantitySelector } from './modules/QuantitySelector/QuantitySelector'

export const ProductConfigurator = () => {
  const { t: tc, locale } = useLocale('common')
  const { t: tp } = useLocale('product-page')
  const { viewModel, productType, productTypeMeta, setFacetOption, setQuantity, addToCart } =
    usePDP()
  const { loadings } = useLoadings()
  const { hasQuantityError, showQuantityError } = useQuantityError()

  const galleryRef = useRef<HTMLDivElement>(null)
  const configuratorRef = useRef<HTMLDivElement>(null)
  const { hasMaxHeight } = useScrollHandler({ galleryRef, configuratorRef })

  const { experimentClient, allVariants } = useExperimentContext() ?? {}

  const abTest = allVariants?.[EXPERIMENTS.galleryExperiment]?.value === 'variant'

  useEffectOnce(() => {
    experimentClient?.exposure(EXPERIMENTS.galleryExperiment)
  }, [experimentClient])

  if (!viewModel) return null

  const title = viewModel?.product?.name?.[locale.code] ?? ''
  const shortDescription = viewModel?.product?.productShortDescription?.[locale.code] ?? ''
  const price = viewModel?.selectedVariant?.finalPrice ?? 0
  const strikedPrice = getVariantStrikedPrice({
    viewModel,
    variant: viewModel.selectedVariant,
    relativeToFacet: 'subscriptionFrequency',
  })
  const maxQuantity = getMaxQuantity(productType)

  return (
    <div className="mt-4 flex w-full flex-col justify-center gap-5 transition-all lg:mt-0 lg:grid lg:grid-cols-[1fr_600px] lg:gap-8 lg:pt-0 xl:gap-12">
      <section className="relative">
        {abTest ? (
          <div className="h-full">
            <ProductGalleryV2 />
          </div>
        ) : (
          <div ref={galleryRef} className={cn('sticky top-0')}>
            <ProductGallery />
          </div>
        )}
      </section>
      <section className="relative">
        <div
          ref={configuratorRef}
          className={cn('grid gap-2 pt-4 lg:overflow-auto lg:pb-12 lg:pt-20', {
            'sticky top-0 -mx-2 h-min px-2 scrollbar-none': !abTest,
            'lg:max-h-screen': hasMaxHeight && !abTest,
          })}
        >
          <ProductInfo
            title={title}
            description={shortDescription}
            finalPrice={formatCartAmount(price, tc)}
            strikedPrice={strikedPrice ? formatCartAmount(strikedPrice, tc) : undefined}
            accolades={viewModel.accolades}
            socialProof={viewModel.socialProof}
            alternateProducts={viewModel?.alternateProducts}
          />
          <FacetRenderer
            facetMeta={productTypeMeta[productType].facets}
            facetOptions={viewModel.facetOptions}
            setFacetOption={setFacetOption}
          />
          {!!hasQuantityError && (
            <Alert type="error" message={tc('error-quantity', { quantity: '5' })} />
          )}
          <div className="flex items-end gap-6">
            {maxQuantity > 1 && (
              <QuantitySelector
                label={tp('quantity')}
                value={viewModel.quantity}
                isInvalid={!!hasQuantityError}
                onChange={(value) => {
                  const validQuantity = getValidQuantity(value, productType)
                  setQuantity(validQuantity)
                  if (value > validQuantity) showQuantityError()
                }}
              />
            )}
            <Button
              data-testid="addToCartBtn"
              className="w-full *:!px-0"
              isLoading={loadings.isAddingLineItems}
              onClick={() => {
                void addToCart()
              }}
            >
              {tp('add-to-cart')}
            </Button>
          </div>
          <ProductDescriptionSection viewModel={viewModel} />
        </div>
      </section>
    </div>
  )
}
